import { useState, useEffect, useCallback } from 'react';

const PHOTOS = [
  {
    src: 'https://static.readdy.ai/image/401a812800a3c57fa50042dc76ab6cbb/e2a70e12228e7f2ecf7e45b1dcbb382f.jpeg',
    alt: 'Кованая скамейка с красными досками и вензелем',
  },
  {
    src: 'https://static.readdy.ai/image/401a812800a3c57fa50042dc76ab6cbb/6cb1802c35f3098438bc84824ffde166.jpeg',
    alt: 'Кованая лавка с тёмно-красными досками',
  },
  {
    src: 'https://static.readdy.ai/image/401a812800a3c57fa50042dc76ab6cbb/d5fcde606dbc5ffa9f9feb7d3c2b384f.jpeg',
    alt: 'Кованая скамейка с красными досками на плитке',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Wrought%20iron%20bench%20with%20soft%20cushion%20and%20decorative%20forged%20elements%20placed%20on%20wooden%20deck%20terrace%20Russia%20private%20house%20summer%20green%20background%20realistic%20photo&width=800&height=600&seq=bench-work-3&orientation=landscape',
    alt: 'Скамейка с подушкой на террасе — готовый объект',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Classic%20black%20wrought%20iron%20bench%20with%20curved%20backrest%20and%20scrollwork%20near%20garden%20path%20flowers%20around%20Russia%20private%20home%20summer%20daylight%20close%20up%20realistic%20photo&width=800&height=600&seq=bench-work-4&orientation=landscape',
    alt: 'Классическая кованая скамья — готовый объект',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Decorative%20forged%20iron%20bench%20with%20intricate%20floral%20pattern%20backrest%20and%20wooden%20slats%20seat%20in%20garden%20gazebo%20Russia%20warm%20light%20realistic%20photo&width=800&height=600&seq=bench-work-5&orientation=landscape',
    alt: 'Скамья с цветочным орнаментом — готовый объект',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Wrought%20iron%20memorial%20cemetery%20bench%20with%20cross%20motif%20and%20decorative%20elements%20installed%20on%20grave%20site%20Russia%20realistic%20photo%20dark%20metal%20finish&width=800&height=600&seq=bench-work-6&orientation=landscape',
    alt: 'Кованая скамья для кладбища — готовый объект',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=Set%20of%20two%20matching%20wrought%20iron%20garden%20benches%20with%20wooden%20seats%20installed%20on%20stone%20patio%20Russia%20private%20estate%20summer%20green%20trees%20realistic%20photo&width=800&height=600&seq=bench-work-7&orientation=landscape',
    alt: 'Парные кованые скамейки — готовый объект',
  },
];

export default function VariantsSection() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const open = (idx: number) => setLightboxIndex(idx);
  const close = () => setLightboxIndex(null);

  const goPrev = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev - 1 + PHOTOS.length) % PHOTOS.length : null));
  }, []);

  const goNext = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev + 1) % PHOTOS.length : null));
  }, []);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightboxIndex, goPrev, goNext]);

  const currentPhoto = lightboxIndex !== null ? PHOTOS[lightboxIndex] : null;

  return (
    <>
      <section className="py-16 md:py-20 bg-background-100">
        <div className="mx-auto px-4 md:px-6" style={{ maxWidth: '1400px' }}>
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
              Примеры наших работ
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              Реальные скамейки и лавки, которые мы уже изготовили для наших клиентов
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
            {PHOTOS.map((photo, idx) => (
              <div
                key={idx}
                role="button"
                tabIndex={0}
                onClick={() => open(idx)}
                onKeyDown={(e) => { if (e.key === 'Enter') open(idx); }}
                className="relative group rounded-md overflow-hidden bg-background-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
              >
                <div className="w-full h-44 md:h-52">
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 text-background-50">
                    <i className="ri-zoom-in-line text-xl"></i>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {currentPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fade-in"
          onClick={close}
        >
          <button
            onClick={close}
            className="whitespace-nowrap absolute top-5 right-5 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 transition-colors cursor-pointer"
            aria-label="Закрыть"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="whitespace-nowrap absolute left-3 md:left-6 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 transition-colors cursor-pointer"
            aria-label="Предыдущее"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="whitespace-nowrap absolute right-3 md:right-6 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 transition-colors cursor-pointer"
            aria-label="Следующее"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>

          <div
            className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentPhoto.src}
              alt={currentPhoto.alt}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <p className="text-background-50/80 text-sm text-center mt-4 max-w-lg">
              {currentPhoto.alt}
            </p>
            <p className="text-background-50/40 text-xs mt-1">
              {lightboxIndex! + 1} / {PHOTOS.length}
            </p>
          </div>
        </div>
      )}
    </>
  );
}